import {
	CAPTIONLESS_MEDIA_KINDS,
	MAX_CAPTION_LENGTH,
	MAX_TEXT_LENGTH,
	visibleTextLength,
	type MediaRef,
} from "@velizhanin-dev/bot-scenario"
import { cn } from "@/shared/lib/utils"
import { Field } from "@/shared/ui/form-field"
import { SegmentedControl } from "@/shared/ui/segmented-control"
import { TextWithVariables } from "@/shared/ui/text-with-variables"
import type { EditorProps } from "./editor-props"
import { readString, withField } from "./editor-props"
import { GalleryField } from "./gallery-field"
import { issuesAt } from "./issues-text"
import { MediaField } from "./media-field"
import { LoadSwap } from "@/shared/ui/load-swap"

const MEDIA_MODES = [
	{ value: "single", label: "Один файл" },
	{ value: "gallery", label: "Альбом" },
] as const

/** Текст и медиа сообщения: общие для «Сообщения» и «Кнопок»; альбом: только у «Сообщения». */
export function MessageBody({
	config,
	onChange,
	issues,
	draft,
	scenarioId,
	allowGallery = false,
}: EditorProps & { allowGallery?: boolean }) {
	const media = config.media as MediaRef | undefined
	const gallery = Array.isArray(config.gallery) ? (config.gallery as MediaRef[]) : undefined
	const hasButtons = Array.isArray(config.buttons)
	const splitForButtons =
		hasButtons && (!!gallery || (config.keyboard !== "reply" && !!media))
	const captioned =
		(!!gallery && !hasButtons) ||
		(!!media && !CAPTIONLESS_MEDIA_KINDS.has(media.kind) && !splitForButtons)
	const limit = captioned ? MAX_CAPTION_LENGTH : MAX_TEXT_LENGTH
	const mode = gallery ? "gallery" : "single"
	const text = readString(config, "text")
	const length = visibleTextLength(text)

	return (
		<>
			<Field
				label={captioned ? "Подпись" : "Текст"}
				htmlFor="block-text"
				issues={issuesAt(issues, "text")}
				hint={
					<span
						className={cn(
							"block text-xs tabular-nums transition-colors",
							length > limit ? "text-red-400" : "text-muted-foreground",
						)}
					>
						{length} из {limit}. Выделите текст, чтобы оформить его
					</span>
				}
			>
				<TextWithVariables
					rich="telegram-html"
					id="block-text"
					value={text}
					onChange={(text) => onChange({ ...config, text })}
					variables={draft.variables}
					rows={5}
				/>
			</Field>
			{splitForButtons ? (
				<p className="-mt-3 text-xs text-muted-foreground">
					{gallery
						? "Альбом отправится отдельно, затем придёт текст с кнопками."
						: "Фото отправится отдельно, затем придёт текст с кнопками."}
				</p>
			) : null}
			<Field
				label="Медиа"
				issues={[...issuesAt(issues, "media", true), ...issuesAt(issues, "gallery", true)]}
			>
				{allowGallery ? (
					<SegmentedControl
						aria-label="Сколько файлов"
						className="mb-2"
						options={MEDIA_MODES}
						value={mode}
						onChange={(value) =>
							onChange(
								value === "gallery"
									? withField(
											withField(config, "media", undefined),
											"gallery",
											media ? [media] : [],
										)
									: withField(config, "gallery", undefined),
							)
						}
					/>
				) : null}
				<LoadSwap loading={false} id={gallery ? "gallery" : "single"}>
					{gallery ? (
						<GalleryField
							scenarioId={scenarioId}
							gallery={gallery}
							onChange={(next) => onChange({ ...config, gallery: next })}
						/>
					) : (
						<MediaField
							scenarioId={scenarioId}
							media={media}
							onChange={(next) => onChange(withField(config, "media", next))}
						/>
					)}
				</LoadSwap>
			</Field>
		</>
	)
}
